import {
  MatchingUsersTypes,
  ApiRequestReducer,
  SearchSessionsMap,
  WelcomeState,
  SeedData,
  AutoplayContext,
} from './types';
import { MatchingUsersActions } from './actions';
import { welcomeStateFromMatchingUsers, welcomeStateFromTimeRangeCounts } from './utils';
import { welcomeStateFromAllTimeCounts } from './selectors';
import { createRequestReducer } from '@fullstory/redux-util';
import { createSearchieCacheReducer, CacheState } from './createSearchieCacheReducer';
import { similarData } from '@fullstory/searchiecache';
import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';
import { GenerateSearchieResponseProto, SearchieType } from '@fullstory/proto/fs/services/alexa';
import { int64 } from '@fullstory/proto/google/protobuf';

const getInitialState = () => ({ loading: false, data: null, error: false });

const isLoading = (state: boolean = false, action: MatchingUsersActions) => {
  switch (action.type) {
    case MatchingUsersTypes.FetchUsersLoading:
      return action.loading;
  }
  return state;
};

const users: ApiRequestReducer<GenerateSearchieResponseProto | null> = (
  state = getInitialState(),
  action,
) => {
  switch (action.type) {
    case MatchingUsersTypes.FetchUsersStart:
      return { ...state, loading: true };
    case MatchingUsersTypes.FetchUsersSuccess:
      return { loading: false, error: false, data: action.data };
    case MatchingUsersTypes.FetchUsersFailure:
      return { ...state, loading: false, error: true };
  }

  return state;
};

const sessions: ApiRequestReducer<SearchSessionsMap | null> = (
  state = getInitialState(),
  action,
) => {
  switch (action.type) {
    case MatchingUsersTypes.FetchSessionsStart:
      return { ...state, loading: true };
    case MatchingUsersTypes.FetchSessionsSuccess:
      return { loading: false, error: false, data: action.data };
    case MatchingUsersTypes.FetchSessionsFailure:
      return { ...state, loading: false, error: true };
  }

  return state;
};

const timeRange = createRequestReducer<GenerateSearchieResponseProto>()(
  MatchingUsersTypes.FetchTimeRangeStart,
  MatchingUsersTypes.FetchTimeRangeSuccess,
  MatchingUsersTypes.FetchTimeRangeFailure,
);

const allTimeRange = createRequestReducer<GenerateSearchieResponseProto>()(
  MatchingUsersTypes.FetchAllTimeRangeStart,
  MatchingUsersTypes.FetchAllTimeRangeSuccess,
  MatchingUsersTypes.FetchAllTimeRangeFailure,
);

const welcomeState = (state: WelcomeState | null = null, action: MatchingUsersActions) => {
  let newState: WelcomeState | null = null;
  switch (action.type) {
    case MatchingUsersTypes.FetchUsersSuccess:
      newState = welcomeStateFromMatchingUsers(action.data?.generatedSearchie?.matchingUsersData);
      break;
    case MatchingUsersTypes.FetchTimeRangeSuccess:
      newState = welcomeStateFromTimeRangeCounts(
        action.data?.generatedSearchie?.timeRangeCountsData,
        action.data?.generatedSearchie?.handle?.segmentDefinition,
      );
      break;
    case MatchingUsersTypes.FetchAllTimeRangeSuccess:
      newState = welcomeStateFromAllTimeCounts(action.data?.generatedSearchie?.timeRangeCountsData);
      break;
  }
  return newState !== null ? newState : state;
};

const cachedMatchingUsers = createSearchieCacheReducer<
  MatchingUsersTypes.FetchUsersCacheUpdate,
  MatchingUsersTypes.FetchUsersCacheSwap,
  GenerateSearchieResponseProto
>(
  MatchingUsersTypes.FetchUsersCacheUpdate,
  MatchingUsersTypes.FetchUsersCacheSwap,
  (d: GenerateSearchieResponseProto) => ({
    computedAt: d?.computedAt,
    cacheOptions: d?.cacheOptions,
  }),
  (
    newData: GenerateSearchieResponseProto | null,
    shownData: GenerateSearchieResponseProto | null,
  ): boolean =>
    // For UX reasons, we want to swap if the old data has a low number of users.
    int64.toNumber(shownData?.generatedSearchie?.matchingUsersData?.matchingCounts?.numUsers) < 5 ||
    similarData(
      SearchieType.SEARCHIE_TYPE_MATCHING_USERS,
      shownData?.generatedSearchie,
      newData?.generatedSearchie,
    ),
);

const defaultSeed: SeedData = {
  users: [],
  numMatchingUsers: 0,
  start: 0,
};
const seedData = (state: SeedData = defaultSeed, action: MatchingUsersActions) => {
  switch (action.type) {
    case MatchingUsersTypes.SeedData:
      return action.data;
    case MatchingUsersTypes.ResetAutoplay:
      return defaultSeed;
  }
  return state;
};

const defaultContext: AutoplayContext = {
  matchingUsersSegmentDefinition: null,
  matchingUsersAdditionalSegmentDefinitions: [],
  searchSessionsSegmentDefinitions: [],
  markersSegmentDefinition: null,
  eventSearch: null,
  currPage: 0,
  pageSize: 0,
  cacheOptions: null,
  name: '',
};
const autoplayContext = (state: AutoplayContext = defaultContext, action: MatchingUsersActions) => {
  switch (action.type) {
    case MatchingUsersTypes.ResetAutoplay:
      return defaultContext;
    case MatchingUsersTypes.SetAutoplayContext:
      return action.data;
  }
  return state;
};

const userFetchCount = (state = 0, action: MatchingUsersActions) => {
  switch (action.type) {
    case MatchingUsersTypes.FetchUsersStart:
      return state + 1;
  }
  return state;
};

type MatchingUsersReducerState = {
  isLoading: ReturnType<typeof isLoading>;
  users: ReturnType<typeof users>;
  sessions: ReturnType<typeof sessions>;
  timeRange: ReturnType<typeof timeRange>;
  allTimeRange: ReturnType<typeof allTimeRange>;
  welcomeState: ReturnType<typeof welcomeState>;
  cachedMatchingUsers: CacheState<GenerateSearchieResponseProto>;
  seedData: ReturnType<typeof seedData>;
  autoplayContext: ReturnType<typeof autoplayContext>;
  userFetchCount: ReturnType<typeof userFetchCount>;
};

export const matchingUsersReducer = combineReducers<MatchingUsersReducerState>({
  isLoading,
  users,
  sessions,
  timeRange,
  allTimeRange,
  welcomeState,
  cachedMatchingUsers,
  seedData,
  autoplayContext,
  userFetchCount,
});
